<template>
	<component
		custom
		class="router-link"
		:disabled="!navigateOnClick"
		:event="navigateOnClick ? 'click' : ''"
		:is="navigateOnClick ? 'router-link' : 'div'"
		:to="{ name: flowDetailsScreenName, params: { wfID: flowData.id, projectIdToReturn: selectedProjectId } }"
	>
		<div
			class="module-card width-100 display-flex flex-row flex-align-center flex-justify-space-between pr-2"
			:class="{ 'is-updating pointer-events-none': isUpdating }"
		>
			<div class="display-flex flex-row flex-align-center">
				<slot name="prepend-inner" />
				<div class="workflow-form-icon-container ml-1 mt-1" @click="(e) => e.preventDefault()">
					<ElementTreeBlockButton
						v-if="showElementButton"
						:element="element"
						:placement="placement"
						:workflow="flowData"
						class="icon-container"
						hide-no-design-message
						:isNative="isNativeElement"
					/>
				</div>
				<FeatureIconsCard class="ml-1 mr-3" :flowData="flowData" />
				<div class="display-flex flex-column flex-justify-center">
					<EditableTextField
						ref="text_field"
						:text="flowTitle"
						class="module-title"
						placeholder="Enter Name"
						v-if="isEditingModuleName"
						@onSave="(v) => (flowTitle = v)"
						@click="(e) => e.preventDefault()"
						:isEditing.sync="isEditingModuleName"
					/>
					<div v-else class="workflow-title">{{ flowData.workflow_title }}</div>
					<FlowFullDescription :showProjectChip="showProjectChip" :flowData="flowData" />
				</div>
			</div>
			<div class="display-flex flex-row flex-align-center">
				<ShareButton dense v-if="canShare && showShareButton" class="share-button" :workflowId="flowData.id" :flowType="flowData.flow_type">
					Share
				</ShareButton>
				<Row class="visibility-container flex-centerize">
					<FeatureVisibilityDropdown
						v-if="showIsOnSwitch && showFeatureVisibility"
						:isVisible="isFlowOn"
						:disabled="!canChangeState"
						:isLoading="loadingVisibilityChange"
						@set-visible="() => (isFlowOn = true)"
						@set-hidden="() => (isFlowOn = false)"
					/>
				</Row>
				<ModuleActivenessStatus
					v-if="showActivityStatus"
					:flowData="flowData"
					class="active-status"
					@open-setup="editConfiguration"
					:hasToggleActivePermissions="hasToggleActivePermissions"
				/>
				<AppDropdownMenu
					left
					bottom
					absolute
					min-width="200"
					content-class="men"
					:items="menuItems"
					v-if="menuItems && menuItems.length !== 0"
				>
					<template #activator>
						<AnemicButton
							@click="(e) => e.preventDefault()"
							width="22"
							height="22"
							icon
							fab
							x-small
							class="flex-centerize menu-button mr-2"
						>
							<SIcon name="three-dot-menu" width="13" class="menu-icon" />
						</AnemicButton>
					</template>
					<template #list-item="{ item }">
						<span
							v-if="!item.divider"
							style="color: #3e495a; font-size: 14px; font-weight: 400"
							:style="{ fontWeight: item.text === 'Edit' ? 600 : 400 }"
						>
							{{ item.text }}
						</span>
					</template>
				</AppDropdownMenu>
				<!-- v-else here cause we need to put a transparent placeholder -->
				<AnemicButton
					fab
					icon
					v-else
					x-small
					rounded
					disabled
					width="22"
					height="22"
					class="flex-centerize mr-2"
					style="background: transparent !important"
				/>
			</div>
			<AppLoader class="app-loader flex-centerize" v-if="showLoader" />
		</div>
	</component>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';

import EditableTextField from '../common/EditableTextField';
import FeatureVisibilityDropdown from '@/components/FeatureVisibility/FeatureVisibilityDropdown.vue';
import ElementTreeBlockButton from '@/components/FlowTree/FlowTreeBlock/ElementTreeBlockButton/ElementTreeBlockButton';
import FeatureIconsCard from '@/components/Modules/FeatureIconsCard.vue';
import ModuleActivenessStatus from '@/components/Modules/ModuleActivenessStatus.vue';
import { getPlacement } from '@/store/modules/placements/getters';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { selectedProjectId } from '@/store/modules/selectedProject/getters';
import { selectWorkflowId } from '@/store/modules/selectedWorkflow/actions';
import {
	toggleWorkflowIsOnSettings,
	updateWorkflow,
	duplicateWorkflow,
	moveWorkflowToOtherProject,
} from '@/store/modules/workflows/actions';
import {
	hasWorkflowEditGeneralConfigurationPermissions,
	hasWorkflowToggleActivePermissions,
	element,
	isNativeElement,
	updatingWorkflowIds,
} from '@/store/modules/workflows/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';
import { isTileElement } from '@/utils/element-service';
import { getTwoWayBindingToStoreMixin } from '@/utils/helpers';
import WorkflowConfigurationResponseService from '@/utils/workflow-configuration-response-service';
import FlowFullDescription from '@/views/Flow/FlowFullDescription.vue';

const twoWayBindingToStoreMixin = getTwoWayBindingToStoreMixin('flowData', 'saveFlow', {
	flowTitle: 'workflow_title',
});

export default {
	name: 'ModuleCard',
	components: {
		FeatureIconsCard,
		EditableTextField,
		FlowFullDescription,
		ModuleActivenessStatus,
		ElementTreeBlockButton,
		FeatureVisibilityDropdown,
		ShareButton: () => import('@/components/Project/share/ShareButton.vue'),
	},
	mixins: [twoWayBindingToStoreMixin],
	props: {
		menuItems: { type: Array },
		flowData: { type: Object },
		navigateOnClick: { type: Boolean, default: true },
		showShareButton: { type: Boolean, default: true },
		showProjectChip: { type: Boolean, default: true },
		showActivityStatus: { type: Boolean, default: true },
		showFeatureVisibility: { type: Boolean, default: true },
		loading: { type: Boolean, default: false },
	},
	computed: {
		...mapGettersShorthand({ selectedProjectId, getElement: element, getPlacement, updatingWorkflowIds }),
		...mapGetters([
			isNativeElement.fullPath,
			projectHasPermission.fullPath,
			hasWorkflowToggleActivePermissions.fullPath,
			hasWorkflowEditGeneralConfigurationPermissions.fullPath,
		]),
		routeName() {
			return this.$route.meta.title ?? this.$route.name;
		},
		flowDetailsScreenName() {
			const extensionPrefix = 'Extension';
			const prefix = this.routeName.includes(extensionPrefix) ? extensionPrefix : '';
			return `${prefix}FlowDetails`;
		},
		canShare() {
			return !!this.flowData?.current_version && this.flowData?.is_published && this.hasSharePermissions;
		},
		canChangeState() {
			return WorkflowService.canHideWorkflow(this.flowData);
		},
		showIsOnSwitch() {
			return WorkflowService.isToggleAddOnToggleable(this.flowData.flow_type, this.flowData.tool_preferences?.command_bar_enabled);
		},
		element() {
			return this.getElement(this.flowData.id);
		},
		placement() {
			return this.getPlacement(this.element.placement);
		},
		isNativeElement() {
			return this[isNativeElement.fullPath](this.flowData.id);
		},
		hasSharePermissions() {
			return this[projectHasPermission.fullPath](this.flowData.project, ProjectPermissions.ChangeWorkflowUsers);
		},

		hasEditGeneralConfigurationPermissions() {
			return this[hasWorkflowEditGeneralConfigurationPermissions.fullPath](this.flowData.id);
		},
		hasToggleActivePermissions() {
			return this[hasWorkflowToggleActivePermissions.fullPath](this.flowData.id);
		},
		userSetupId() {
			return this.flowData.current_version_user_setup;
		},
		generalSetupId() {
			return this.flowData.current_version_general_setup;
		},
		isFlowOn: {
			get() {
				return this.flowData.user_settings?.is_on;
			},
			async set(newV) {
				if (newV !== undefined && this.isFlowOn !== newV) {
					this.loadingVisibilityChange = true;
					try {
						await this.toggleWorkflowIsOnSettings({ id: this.flowData.id });
					} catch {
						this.$toast.error("Couldn't change feature visibility, please try again later");
					} finally {
						this.loadingVisibilityChange = false;
					}
				}
			},
		},
		isUpdating() {
			return this.updatingWorkflowIds.includes(this.flowData.id);
		},
		showLoader() {
			return this.isUpdating || this.loading;
		},
		showElementButton() {
			return !!this.element && !isTileElement(this.element.type);
		},
	},
	methods: {
		...mapActions([updateWorkflow.fullPath, selectWorkflowId.fullPath, moveWorkflowToOtherProject.fullPath]),
		...mapActionsShorthand({ duplicateWorkflow, toggleWorkflowIsOnSettings }),
		async saveFlow(data) {
			await this[updateWorkflow.fullPath]({ id: this.flowData.id, data }); // directly call store's updateWorkflow to be synced with server
		},

		renameWorkflow() {
			this.isEditingModuleName = true;
			setTimeout(() => {
				this.$refs.text_field.focus();
			}, 100);
		},
		editConfiguration() {
			this[selectWorkflowId.fullPath]({ id: this.flowData.id });
			return WorkflowConfigurationResponseService.editAndSaveConfiguration(
				{
					generalConfigurationId: this.hasEditGeneralConfigurationPermissions ? this.generalSetupId : null,
					userSpecificConfigurationId: this.userSetupId,
				},
				false
			);
		},
	},
	data() {
		return {
			isEditingModuleName: false,
			loadingVisibilityChange: false,
		};
	},
};
</script>

<style scoped lang="scss">
.router-link {
	text-decoration: none !important;
}
.module-card {
	position: relative;

	height: 70px;
	border-radius: 13px;
	background-color: var(--bg-color);

	&:hover {
		cursor: pointer;
		transition: 0.1s;
		background: #f5f7fa;

		.project-type-chip {
			border: 1px solid var(--border-color);
		}

		.share-button {
			opacity: 1;
			transition: 0.3s;
		}

		.main-section .btn-click-to-edit {
			opacity: 1;
			transition: 0.8s;
		}

		.menu-button {
			border: 1px solid var(--forth-border-color);
		}
	}

	&:not(:hover) {
		transition: 0.1s;
		::v-deep {
			.ply_button_icon {
				transition: 0.1s;
				background-color: #90909070 !important;
			}
		}
	}

	&.is-updating {
		.app-loader {
			position: absolute;
			content: '';
			height: 100%;
			width: 100%;
			border-radius: 13px;
			opacity: 0.8;
			background: #f8f8f8;
		}
	}
}

.workflow-form-icon-container {
	width: 34px;
}

.workflow-title {
	font-size: 15px;
	font-weight: normal;
	color: var(--primary-font-color);

	overflow: hidden;
	display: -webkit-box;
	text-overflow: ellipsis;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2; // num of rows to break
}

.project-type-chip {
	width: 54px;
	height: 16px;
	font-size: 12px;
	color: #9e9e9e;
	font-weight: 400;
	border-radius: 4px;
	background: #f5f5f5;
}
.active-status {
	margin: 0px 40px 0px 30px;
}
.share-button {
	width: 88px !important;
	height: 22px !important;
	padding: 0px !important;
	border: 1px solid #9e9e9e;
	border-radius: 5px !important;
	background: transparent !important;

	font-weight: 700;
	color: #4d4c52;
	margin-right: 40px;
	font-size: 13px !important;

	opacity: 0;
	transition: 0.3s;
}
.menu-button {
	background: transparent !important;
	.menu-icon {
		margin-left: -1px;
	}
}
.icon-container {
	transform: scale(0.8);
}
.visibility-container {
	min-width: 54px;
}
</style>
